import { FunctionComponent, RefObject } from 'react';
import { EntityList } from '../../../entity-list/EntityList';
import { Filters } from '../../../filter/Filters';
import { ListMetaNetworkParams } from '../../../list-conductor/list-meta-data';
import { PipelineListTable } from '../../../pipelines/PipelineList/components/PipelineListTable/PipelineListTable';
import { QueryResult } from '../../../request/hooks/useQuery';
import { Integration } from '../../../shared/interfaces/integration';
import { PIPELINE_SUBSCRIPTIONS_FILTERS } from '../../constants';
import { SEARCH_FILTER_KEY } from '../Utils';
import { EmptyWidgetProps } from '../../../shared/components/EmptyWidget/EmptyWidget';

interface PipelineSubscriptionListProps<S extends object, T extends object> {
  fetch: (params?: ListMetaNetworkParams) => Promise<QueryResult<Integration[]>>;
  selectedItems: Set<number | string>;
  onItemSelectionChange: (row: Integration, selected: boolean) => void;
  onAllItemsSelectionChange: (selected: boolean) => void;
  onAllInPageSelectionChange: (selected: boolean) => void;
  StandaloneModeActions?: FunctionComponent<S>;
  standaloneModeActionsProps?: S;
  BulkActionsComponent?: FunctionComponent;
  bulkActionsComponentProps?: object;
  innerRef?: RefObject<unknown>;
  newListConnectedTitle?: string;
  listIcon?: string;
  newListComponentProps?: Partial<EmptyWidgetProps>;
  customNewListComponentProps?: T;
  onRefresh?: () => void;
  entity: string;
}

export function PipelineSubscriptionsList<S extends object, T extends object>({
  selectedItems,
  onAllInPageSelectionChange,
  onAllItemsSelectionChange,
  onItemSelectionChange,
  entity,
  fetch,
  innerRef,
  ...rest
}: PipelineSubscriptionListProps<S, T>) {
  return (
    <EntityList
      newListConnectedTitle=''
      listIcon=''
      innerRef={innerRef}
      mode='standalone'
      genericErrorTitle='Error while fetching Pipelines'
      genericErrorSubTitle='Retry Loading the Pipelines'
      ListComponent={PipelineListTable}
      searchKey={SEARCH_FILTER_KEY}
      emptyTitle='No Matching Pipelines Found'
      emptySubTitle='Modify search term to find the desired Pipelines'
      dataSource$={fetch}
      showSearch
      showFilters
      inMemoryList
      FiltersComponent={Filters}
      filtersComponentProps={{
        filters: PIPELINE_SUBSCRIPTIONS_FILTERS,
        entity
      }}
      searchPlaceholder='Search Subscribed Pipelines'
      bulkSelection
      selectedItems={selectedItems}
      onAllInPageSelectionChange={onAllInPageSelectionChange}
      onAllItemsSelectionChange={onAllItemsSelectionChange}
      onItemSelectionChange={onItemSelectionChange}
      shimmerColumnsCount={4}
      listComponentProps={{
        hideColumns: ['config.sync_frequency_schedule', 'status'],
        sortThroughHeader: {
          name: 'pipeline_name',
          source: 'source',
          destination: 'destination',
          createdBy: 'created_by'
        },
        tableProps: {
          disableClick: true,
          withoutShadow: entity === 'add-pipeline',
          entity: 'Pipeline',
          compactHeader: true,
          classes: {
            containerClass: entity === 'add-pipeline' ? 'border border-radius-md' : ''
          }
        }
      }}
      entity='subscribed-pipelines'
      {...rest}
    />
  );
}
